import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import userService, { 
  UserProfile, 
  SubscriptionDetails, 
  SettingsPreferences 
} from '../services/userService';
import { User } from '../services/authService';

// Define user store state
interface UserState {
  // Profile and preferences
  profile: UserProfile | null;
  currentUser: User | null;
  subscription: SubscriptionDetails | null;
  settings: SettingsPreferences | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchCurrentUser: () => Promise<void>;
  fetchProfile: () => Promise<void>;
  fetchSubscription: () => Promise<void>;
  subscribeToPlan: (planId: string) => Promise<void>;
  cancelSubscription: () => Promise<void>;
  clearError: () => void;
}

// Default settings
const defaultSettings: SettingsPreferences = {
  emailNotifications: true,
  darkMode: false,
  language: 'en',
  videoQuality: 'medium',
  audioEnabled: true,
  videoEnabled: true,
  interviewLanguage: 'en',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

// Create the user store with persistence
export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      profile: null,
      currentUser: null,
      subscription: null,
      settings: defaultSettings,
      isLoading: false,
      error: null,

      // Fetch current user basic info
      fetchCurrentUser: async () => {
        set({ isLoading: true, error: null });
        try {
          const user = await userService.getCurrentUser();
          set({ currentUser: user, isLoading: false });
        } catch (error: any) {
          set({ 
            error: error.message || 'Failed to fetch user',
            isLoading: false
          });
        }
      },

      // Fetch user profile
      fetchProfile: async () => {
        set({ isLoading: true, error: null });
        try {
          const profile = await userService.getUserProfile();
          set({ profile, isLoading: false });
        } catch (error: any) {
          set({ 
            error: error.message || 'Failed to fetch profile',
            isLoading: false
          });
        }
      },

      // Fetch subscription details
      fetchSubscription: async () => {
        set({ isLoading: true, error: null });
        try {
          const subscription = await userService.getSubscription();
          set({ subscription, isLoading: false });
        } catch (error: any) {
          set({ 
            error: error.message || 'Failed to fetch subscription',
            isLoading: false
          });
        }
      },

      // Subscribe to a plan
      subscribeToPlan: async (planId: string) => {
        set({ isLoading: true, error: null });
        try {
          const subscription = await userService.subscribeToPlan(planId);
          set({ subscription, isLoading: false });
        } catch (error: any) {
          set({ 
            error: error.message || 'Failed to subscribe to plan',
            isLoading: false
          });
        }
      },

      // Cancel subscription
      cancelSubscription: async () => {
        set({ isLoading: true, error: null });
        try {
          const subscription = await userService.cancelSubscription();
          set({ subscription, isLoading: false });
        } catch (error: any) {
          set({ 
            error: error.message || 'Failed to cancel subscription',
            isLoading: false
          });
        }
      },
      // Clear error message
      clearError: () => set({ error: null }),
    }),
    {
      name: 'mocktrail-user-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist these fields
      partialize: (state) => ({ 
        settings: state.settings
      }),
    }
  )
); 